import { AddressLookupOrInput } from '@/ui/components/addressLookupOrInput/AddressLookupOrInput';
import {
  DateInput,
  Field,
  RawInput as Input,
  Radio,
  Select,
} from '@payaca/ambrosia';

import { formatDate } from 'date-fns';
import { FC, InputHTMLAttributes, useEffect } from 'react';
import { Controller, get, useFormContext, useWatch } from 'react-hook-form';
import DeviceSearchField from './DeviceSearchField';
import DiscriminatorField from './DiscriminatorField';
import { MeterNumberInputField } from './MeterNumberInputField';
import ProjectFileSelection from './ProjectFileSelection';
import { DisplayIfCondition } from './utils/devices';
import { getFieldLabelFromFieldSchema } from './utils/display';
import {
  fieldValidationState,
  getAddressValidationState,
} from './utils/reactHookForm';

export type Props = {
  name: string;
  fieldSchema: any;
  namespace?: string;
  definitions: Record<string, any>;
  dependentFields: string[];
};

const addressLines = ['line1', 'line2', 'city', 'postcode', 'country'];

export const DynamicField: FC<Props> = ({
  name,
  namespace,
  fieldSchema,
  definitions,
  dependentFields,
}) => {
  const {
    control,
    register,
    watch,
    formState: { errors },
    setValue,
    getValues,
  } = useFormContext();

  const displayIf = fieldSchema.json_schema_extra.displayIf;

  let displayIfArray: DisplayIfCondition[] = [];
  if (Array.isArray(displayIf)) {
    displayIfArray = displayIf;
  } else if (displayIf) {
    displayIfArray = [displayIf];
  }
  if (displayIfArray.length) {
    const allConditionsMet = displayIfArray.every(
      (condition: DisplayIfCondition) => {
        const namespacedProperty = `${namespace}.${condition.property}`;
        return (
          ('value' in condition &&
            condition.value &&
            watch(namespacedProperty) === condition.value) ||
          ('valueIsSet' in condition &&
            condition.valueIsSet &&
            watch(namespacedProperty))
        );
      }
    );
    if (!allConditionsMet) {
      return null;
    }
  }

  const registerProps = { shouldUnregister: !!displayIf };
  const helperText = fieldSchema.json_schema_extra.tooltip;

  const fieldLabel = getFieldLabelFromFieldSchema(fieldSchema);

  switch (fieldSchema.json_schema_extra.type) {
    case 'USER ACCOUNT DERIVED INPUT':
    case 'TEXT INPUT': {
      let specificFieldSchema = fieldSchema;
      // In the event that their are multiple valid inputs, favour the most
      // specific one (e.g. number over string)
      if (fieldSchema.anyOf) {
        specificFieldSchema = fieldSchema.anyOf.find((item: any) => {
          return item.type !== 'string';
        });
      }
      const extraInputProps: InputHTMLAttributes<HTMLInputElement> = {};
      if (specificFieldSchema.type === 'number') extraInputProps.step = '0.01';
      return (
        <Field.Legacy key={name} {...fieldProps(errors, name)}>
          <Field.Label>{fieldLabel}</Field.Label>
          <Field.HelperText>{helperText}</Field.HelperText>
          <Input
            type={specificFieldSchema.type}
            placeholder={fieldSchema.json_schema_extra.placeholderText}
            onWheel={(e) => e.currentTarget.blur()}
            {...extraInputProps}
            {...register(name, {
              ...registerProps,
              setValueAs: (value) => {
                if (specificFieldSchema.type === 'number')
                  return value ? Number.parseFloat(value) : null;

                return value;
              },
            })}
          />
        </Field.Legacy>
      );
    }

    case 'RADIO INPUT':
      return (
        <Field.Legacy key={name} {...fieldProps(errors, name)}>
          <Field.Label>{fieldLabel}</Field.Label>
          <Field.HelperText>{helperText}</Field.HelperText>
          {/* We have to use a controlled component to allow complex radio inputs with differing types for each value */}
          <Controller
            control={control}
            name={name}
            render={({ field: { onChange, ...rest } }) => (
              <>
                {Object.entries<any>(fieldSchema.json_schema_extra.options).map(
                  ([key, value]) => (
                    <Radio
                      key={key}
                      label={key}
                      onChange={() => {
                        onChange(value);
                        // clear the fields that are dependent on this field
                        dependentFields.forEach((dependentFieldKey) => {
                          setValue(dependentFieldKey, undefined);
                        });
                      }}
                      checked={rest.value === value}
                      {...rest}
                    />
                  )
                )}
              </>
            )}
          />
        </Field.Legacy>
      );
    case 'SELECT INPUT':
      return (
        <Field.Legacy key={name} {...fieldProps(errors, name)}>
          <Field.Label>{fieldLabel}</Field.Label>
          <Field.HelperText>{helperText}</Field.HelperText>
          <Controller
            control={control}
            name={name}
            render={({ field: { ref, value, ...rest } }) => (
              <Select
                {...rest}
                value={value || null}
                options={fieldSchema.json_schema_extra.options.selectOptions.map(
                  (option: any) => {
                    return { label: option, value: option };
                  }
                )}
                placeholder={fieldSchema.json_schema_extra.placeholderText}
              />
            )}
          />
        </Field.Legacy>
      );
    case 'DERIVED INPUT':
      return (
        <DerivedInputField
          fieldSchema={fieldSchema}
          name={name}
          namespace={namespace}
          registerProps={registerProps}
        />
      );
    case 'DATE INPUT':
      return (
        <Field.Legacy key={name} {...fieldProps(errors, name)}>
          <Field.Label>{fieldLabel}</Field.Label>
          <Field.HelperText>{helperText}</Field.HelperText>
          <Controller
            control={control}
            name={name}
            render={({ field: { ref, value, onChange, ...rest } }) => (
              <DateInput
                value={value?.length > 0 ? new Date(value) : null}
                onChange={(date) => {
                  onChange(formatDate(date, 'yyyy-MM-dd'));
                }}
                {...rest}
              />
            )}
          />
        </Field.Legacy>
      );
    case 'FIXED INPUT':
      return <input type="hidden" {...register(name, registerProps)} />;
    case 'FIXED INPUT AS TITLE':
      return (
        <Controller
          control={control}
          name={name}
          render={({ field: { value } }) => (
            <>
              <h2>{value}</h2>
              <input type="hidden" {...register(name, registerProps)} />
            </>
          )}
        />
      );
    case 'ADDRESS RECOGNISED INPUT': {
      return (
        <Field.Legacy key={name} name={name}>
          <Field.Label>{fieldLabel}</Field.Label>
          <Field.HelperText>{helperText}</Field.HelperText>
          <Controller
            control={control}
            name={name}
            render={({ field: { ref, value, ...rest } }) => {
              // The address component won't render validation errors unless it thinks the field has been touched
              const fakeAllTouched: { [fieldName: string]: boolean } =
                Object.fromEntries(addressLines.map((line) => [line, true]));

              return (
                <AddressLookupOrInput
                  {...rest}
                  address={value}
                  touchedState={fakeAllTouched}
                  validationState={getAddressValidationState(errors, name)}
                />
              );
            }}
          />
        </Field.Legacy>
      );
    }
    case 'MPAN RECOGNISED INPUT':
      return (
        <MeterNumberInputField
          name={name}
          fieldSchema={fieldSchema}
          helperText={helperText}
          registerProps={registerProps}
        />
      );
    case 'SEARCH DEVICE RECOGNISED INPUT': {
      const [deviceType] = getValues([`${namespace}.deviceType`]);
      return (
        <DeviceSearchField
          fieldSchema={fieldSchema}
          fieldProps={fieldProps}
          name={name}
          deviceType={deviceType}
        />
      );
    }
    case 'G100_SEARCH_RECOGNISED_INPUT_01': {
      return (
        <DeviceSearchField
          fieldSchema={fieldSchema}
          fieldProps={fieldProps}
          name={name}
          deviceType="G100_DEVICE"
        />
      );
    }
    case 'NESTED INPUT FORM DISCRIMINATED UNION 01': {
      // this should show a select field which on selection would conditionally show further schema
      return (
        <DiscriminatorField
          fieldSchema={fieldSchema}
          name={name}
          namespace={namespace}
          definitions={definitions}
        />
      );
    }

    case 'SINGLE ATTACHMENT INPUT 01': {
      return (
        <Field.Legacy>
          <Field.Label>{fieldLabel}</Field.Label>
          <ProjectFileSelection name={name} formSection={fieldSchema} />
        </Field.Legacy>
      );
    }

    default:
      throw new Error(`Unknown type: ${fieldSchema.json_schema_extra.type}`);
  }
};

export const fieldProps = (errors: any, name: string) => {
  const fieldError = get(errors, name);
  return {
    name,
    validationState: fieldError && fieldValidationState(fieldError),
  };
};

const DerivedInputField: FC<{
  fieldSchema: any;
  name: string;
  namespace?: string;
  registerProps: any;
}> = ({ fieldSchema, name, namespace, registerProps }) => {
  const { control, register, setValue } = useFormContext();

  const { source, sourceOutputMappings } =
    fieldSchema.json_schema_extra.derivedValue;
  const sourceValue = useWatch({ control, name: `${namespace}.${source}` });
  const derivedValue =
    sourceValue && sourceOutputMappings[sourceValue.toString()];

  useEffect(() => {
    setValue(name, derivedValue);
  }, [sourceValue, name, setValue, derivedValue]);

  return (
    <input
      type="hidden"
      {...register(name, {
        ...registerProps,
        value: derivedValue,
      })}
    />
  );
};
